/*
 * RocketAEO shoppable product cards for Shopify blog articles.
 *
 * Replaces the "View product" CTA on the product cards in our article HTML with
 * a size picker, live price and stock, and an Add to cart button. The card's
 * image (and title, where it is linked) still opens the product page. Product
 * data comes live from the store (/products/<handle>.js); nothing is synced or
 * hardcoded.
 *
 * Without this script the cards stay as they were published, so it is safe to
 * remove at any time.
 *
 * Cards it upgrades, inside the article:
 *   1. Whole-card links (LNGVTY, The Stack): <a href="/products/<handle>"> with an
 *      <img> and a "View product" label inside it.
 *   2. Separate CTA links (MetaShot): an image link plus its own
 *      <a href="/products/<handle>">View product</a> button.
 *   3. Any element with data-rocket-product="<handle>"; optional
 *      data-rocket-variant="<variant id>" picks the default size.
 *
 * Cart: opens GoKwik KwikCart when the store uses it, else Dawn's cart
 * notification or drawer (same as Dawn's own product form), else goes to /cart.
 *
 * Tracking: Shopify emits its standard product_added_to_cart event for the add,
 * which the Google & YouTube app already sends to GA4 as add_to_cart, so this
 * script never sends add_to_cart itself. It publishes custom events for the
 * Rocket custom pixel (rocket-shoppable-pixel.js), which sends view_item_list,
 * select_item and blog_add_to_cart to GA4:
 *   rocket_blog_card_viewed, rocket_blog_card_clicked, rocket_blog_add_to_cart
 */
(function () {
    'use strict';

    if (window.RocketShoppable) return;

    const SCOPE = '.article-template, .rocket-shopify-article, article, main';
    const VIEW_PRODUCT = /^\s*view product\s*(\u2192|->)?\s*$/i;
    const productCache = {};

    const css = `
.rkt-card{display:flex;flex-direction:column}
.rkt-card>a.rkt-card__link{display:block;color:inherit;text-decoration:none;flex:1 1 auto}
.rkt-card>.rkt-buy{padding:0 16px 18px}
.rkt-buy{display:flex;flex-direction:column;gap:10px;font-family:inherit}
.rkt-buy__price{display:flex;align-items:baseline;gap:8px;font-size:15px;font-weight:700;color:inherit}
.rkt-buy__compare{font-size:13px;font-weight:400;opacity:.55;text-decoration:line-through}
.rkt-buy__select{width:100%;padding:9px 10px;border:1px solid rgba(0,0,0,.25);border-radius:0;background:#fff;color:#181818;font:inherit;font-size:14px}
.rkt-buy__btn{display:block;width:100%;padding:12px 14px;border:0;border-radius:0;background:var(--rkt-btn-bg,#181818);color:var(--rkt-btn-fg,#fff);font:inherit;font-size:12px;font-weight:700;letter-spacing:1px;text-transform:uppercase;text-align:center;cursor:pointer}
.rkt-buy__btn[disabled]{opacity:.45;cursor:not-allowed}
.rkt-buy__msg{font-size:12px;color:#b42318}
.rkt-buy__msg:empty{display:none}
`;

    function injectStyles() {
        if (document.getElementById('rkt-shoppable-css')) return;
        const style = document.createElement('style');
        style.id = 'rkt-shoppable-css';
        style.textContent = css;
        document.head.appendChild(style);
    }

    function root() {
        return window.Shopify && Shopify.routes ? Shopify.routes.root : '/';
    }

    function currency() {
        return (window.Shopify && Shopify.currency && Shopify.currency.active) || 'INR';
    }

    function handleFromUrl(href) {
        const match = /\/products\/([^/?#]+)/.exec(href || '');
        return match ? decodeURIComponent(match[1]) : null;
    }

    function formatMoney(cents) {
        try {
            return new Intl.NumberFormat('en-IN', { style: 'currency', currency: currency(), maximumFractionDigits: cents % 100 ? 2 : 0 }).format(cents / 100);
        } catch (e) {
            return (cents / 100).toFixed(2);
        }
    }

    async function getProduct(handle) {
        if (!productCache[handle]) {
            productCache[handle] = fetch(`${root()}products/${encodeURIComponent(handle)}.js`).then((res) => {
                if (!res.ok) throw new Error(`Product ${handle}: HTTP ${res.status}`);
                return res.json();
            });
        }
        return productCache[handle];
    }

    // Each card: { type, host, cta, handle, variant }. host is where the buy block goes.
    function findCards() {
        const cards = [];
        const seen = new Set();

        document.querySelectorAll('[data-rocket-product]').forEach((el) => {
            if (el.dataset.rktDone) return;
            seen.add(el);
            const cta = [...el.querySelectorAll('a, span')].find((n) => n.children.length === 0 && VIEW_PRODUCT.test(n.textContent)) || null;
            cards.push({ type: 'explicit', host: el, cta, handle: el.dataset.rocketProduct, variant: el.dataset.rocketVariant });
        });

        // SCOPE selectors can nest (article inside main), so skip anything already collected.
        document.querySelectorAll(SCOPE).forEach((scope) => {
            scope.querySelectorAll('a[href*="/products/"]').forEach((a) => {
                if (seen.has(a) || a.dataset.rktDone || a.closest('[data-rocket-product], .rkt-card, .rkt-buy, header, footer, cart-drawer')) return;
                seen.add(a);
                const handle = handleFromUrl(a.getAttribute('href'));
                if (!handle) return;

                if (a.querySelector('img')) {
                    const label = [...a.querySelectorAll('span')].find((s) => s.children.length === 0 && VIEW_PRODUCT.test(s.textContent));
                    if (label) cards.push({ type: 'link', host: a, cta: label, handle });
                } else if (VIEW_PRODUCT.test(a.textContent)) {
                    cards.push({ type: 'cta', host: a.parentElement, cta: a, handle });
                }
            });
        });
        return cards;
    }

    // An <a> card cannot hold a <select> or <button>, so wrap it: the wrapper takes
    // over the card's box styles and the link keeps the image, title and text.
    function wrapLinkCard(a) {
        const wrapper = document.createElement('div');
        wrapper.className = 'rkt-card';
        wrapper.setAttribute('style', a.getAttribute('style') || '');
        wrapper.style.display = 'flex';
        wrapper.style.flexDirection = 'column';
        a.parentNode.insertBefore(wrapper, a);
        wrapper.appendChild(a);
        a.removeAttribute('style');
        a.classList.add('rkt-card__link');
        return wrapper;
    }

    // The card element that holds the image, used for impressions and click tracking.
    function cardElement(card) {
        if (card.type === 'link') return card.host.closest('.rkt-card') || card.host;
        let el = card.host;
        while (el && el.parentElement && !el.querySelector('img')) el = el.parentElement;
        return el || card.host;
    }

    function pickDefault(product, preferredId) {
        const preferred = product.variants.find((v) => String(v.id) === String(preferredId) && v.available);
        return preferred || product.variants.find((v) => v.available) || product.variants[0];
    }

    function eventData(product, variant, position) {
        return {
            list_name: `Blog: ${document.title.split(/\s[\u2013|-]\s/)[0]}`,
            list_id: location.pathname,
            position,
            currency: currency(),
            product_id: String(product.id),
            variant_id: String(variant.id),
            product_title: product.title,
            variant_title: variant.title,
            price: variant.price / 100,
            vendor: product.vendor || ''
        };
    }

    function publish(name, data) {
        try {
            if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
                Shopify.analytics.publish(name, data);
            }
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({ event: name, rocket: data });
        } catch (e) {
            console.error('RocketShoppable: tracking failed', name, e);
        }
    }

    const impressions = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting || !entry.target.rktEvent) return;
            publish('rocket_blog_card_viewed', entry.target.rktEvent());
            impressions.unobserve(entry.target);
        });
    }, { threshold: 0.5 }) : null;

    function cartUi() {
        if (window.kwikCartActive && typeof window.openGokwikSideCart === 'function') return { gokwik: true };
        const dawn = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (dawn && typeof dawn.renderContents === 'function' && typeof dawn.getSectionsToRender === 'function') return { dawn };
        return {};
    }

    async function addToCart(variantId) {
        const ui = cartUi();
        const body = { id: Number(variantId), quantity: 1 };
        if (ui.dawn) {
            body.sections = ui.dawn.getSectionsToRender().map((s) => s.id);
            body.sections_url = location.pathname;
        }
        const res = await fetch(`${root()}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body)
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.status) throw new Error(data.description || data.message || `HTTP ${res.status}`);
        return { data, ui };
    }

    function openCart(result) {
        if (result.ui.gokwik) {
            window.openGokwikSideCart();
            return;
        }
        if (result.ui.dawn) {
            result.ui.dawn.classList.remove('is-empty');
            result.ui.dawn.renderContents(result.data);
            return;
        }
        window.location.href = `${root()}cart`;
    }

    function render(card, product, position) {
        const productUrl = `${root()}products/${product.handle}`;
        let variant = pickDefault(product, card.variant);

        const buy = document.createElement('div');
        buy.className = 'rkt-buy';

        const price = document.createElement('div');
        price.className = 'rkt-buy__price';

        let select = null;
        if (product.variants.length > 1) {
            select = document.createElement('select');
            select.className = 'rkt-buy__select';
            const option = product.options[0] ? product.options[0].name || product.options[0] : 'Option';
            select.setAttribute('aria-label', `${option} for ${product.title}`);
            product.variants.forEach((v) => {
                const opt = document.createElement('option');
                opt.value = v.id;
                opt.textContent = v.available ? v.title : `${v.title} (sold out)`;
                if (v.id === variant.id) opt.selected = true;
                select.appendChild(opt);
            });
        }

        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'rkt-buy__btn';
        // A separate CTA button (MetaShot) already carries the brand's button style.
        if (card.type === 'cta' && card.cta.getAttribute('style')) {
            btn.setAttribute('style', card.cta.getAttribute('style'));
            btn.style.display = 'block';
            btn.style.width = '100%';
            btn.style.border = '0';
            btn.style.cursor = 'pointer';
        }

        const msg = document.createElement('div');
        msg.className = 'rkt-buy__msg';
        msg.setAttribute('role', 'status');

        function update() {
            const compare = variant.compare_at_price && variant.compare_at_price > variant.price
                ? `<span class="rkt-buy__compare">${formatMoney(variant.compare_at_price)}</span>` : '';
            price.innerHTML = `<span>${formatMoney(variant.price)}</span>${compare}`;
            btn.disabled = !variant.available;
            btn.textContent = variant.available ? 'Add to cart' : 'Sold out';
            msg.textContent = '';
        }

        if (select) {
            select.addEventListener('change', () => {
                variant = product.variants.find((v) => String(v.id) === select.value) || variant;
                update();
            });
        }

        btn.addEventListener('click', async () => {
            if (!variant.available) return;
            btn.disabled = true;
            btn.textContent = 'Adding\u2026';
            try {
                const result = await addToCart(variant.id);
                publish('rocket_blog_add_to_cart', eventData(product, variant, position));
                btn.textContent = 'Added';
                openCart(result);
                setTimeout(update, 1500);
            } catch (e) {
                console.error('RocketShoppable: add to cart failed', e);
                msg.textContent = 'Could not add this. Opening the product page\u2026';
                setTimeout(() => { window.location.href = productUrl; }, 1200);
            }
        });

        buy.appendChild(price);
        if (select) buy.appendChild(select);
        buy.appendChild(btn);
        buy.appendChild(msg);

        if (card.type === 'link') {
            const wrapper = wrapLinkCard(card.host);
            card.cta.remove();
            wrapper.appendChild(buy);
        } else if (card.cta) {
            card.cta.replaceWith(buy);
        } else {
            card.host.appendChild(buy);
        }
        update();

        const el = cardElement(card);
        el.rktEvent = () => eventData(product, variant, position);
        if (impressions) impressions.observe(el);
        el.addEventListener('click', (event) => {
            const link = event.target.closest('a[href*="/products/"]');
            if (link && el.contains(link)) publish('rocket_blog_card_clicked', eventData(product, variant, position));
        });
    }

    async function init() {
        injectStyles();
        const cards = findCards();
        await Promise.all(cards.map(async (card, index) => {
            card.host.dataset.rktDone = '1';
            try {
                const product = await getProduct(card.handle);
                // Fully sold out: keep the card as published so the CTA still opens the product page.
                if (!product.variants.some((v) => v.available)) return;
                render(card, product, index + 1);
            } catch (e) {
                console.error('RocketShoppable: left card as published', card.handle, e);
            }
        }));
        return cards.length;
    }

    window.RocketShoppable = { init };
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }
})();
